/**
 * Font visibility rules.
 *
 * fontAccess.json on the CDN says which fonts are limited to which business
 * profiles, per environment:
 *
 *   { "version": 1, "fonts": { "Some Font": { "testing": [100], "production": [1000] } } }
 *
 * A font that is not listed is visible to everybody. A font that is listed is
 * visible only to the listed businessProfileIds, and only in the listed
 * environments. bpIds sit under the environment because the same number is a
 * different business profile on testing than on production.
 *
 * Fetched at runtime and never bundled, so a rule change takes effect without a
 * redeploy. If it cannot be fetched we show every font.
 *
 * Note this filters the *picker* only. fontLoader keeps the full list, so a
 * saved creative using a restricted font still renders.
 */
import { ref } from 'vue';
import type { FontAccessRules } from '@/types/library/fontAccess';

const base = (import.meta.env.VITE_CB_FONTS_URL as string) || '';
const RULES_URL = `${base.replace(/\/+$/, '')}/fontAccess.json`;

/** null means "no rules known" - callers must read that as "show everything". */
export const fontAccessRules = ref<FontAccessRules | null>(null);

let pending: Promise<FontAccessRules | null> | null = null;

export function loadFontAccess(): Promise<FontAccessRules | null> {
  if (!pending) {
    pending = fetch(RULES_URL, { cache: 'no-cache' })
      .then(res => (res.ok ? res.json() : null))
      .then((data: unknown) => {
        const rules =
          data &&
          typeof data === 'object' &&
          'fonts' in (data as FontAccessRules)
            ? (data as FontAccessRules)
            : null;
        fontAccessRules.value = rules;
        return rules;
      })
      .catch(() => {
        fontAccessRules.value = null;
        return null;
      });
  }
  return pending;
}

/** Only needed by tests. */
export function resetFontAccess(): void {
  pending = null;
  fontAccessRules.value = null;
}

/**
 * No rules, or no rule for this font, means visible. A rule with no entry for
 * the current environment means hidden - an environment that is not listed is
 * not allowed, rather than allowed by default.
 */
export function isFontVisible(
  rules: FontAccessRules | null,
  familyName: string,
  releaseStage: string | undefined,
  businessProfileId: number | null | undefined
): boolean {
  if (!rules || !rules.fonts) return true;

  const rule = rules.fonts[familyName];
  if (!rule) return true;
  if (!releaseStage) return false;

  const allowed = rule[releaseStage];
  if (!Array.isArray(allowed)) return false;

  return allowed.map(Number).includes(Number(businessProfileId));
}

export function filterFontNames(
  names: string[],
  rules: FontAccessRules | null,
  releaseStage: string | undefined,
  businessProfileId: number | null | undefined
): string[] {
  if (!rules) return names;
  return names.filter(name =>
    isFontVisible(rules, name, releaseStage, businessProfileId)
  );
}
